interface AuthErrorLike {
  status?: number
  code?: string | null
  message?: string | null
}

export const DEFAULT_RESET_EMAIL_COOLDOWN_SECONDS = 60

const RESET_EMAIL_COOLDOWN_STORAGE_KEY = 'auth:reset-email-cooldown-until'

function getStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return window.localStorage
  } catch {
    return null
  }
}

function parseCooldownUntil(rawValue: string | null): number | null {
  if (!rawValue) {
    return null
  }

  const parsedValue = Number.parseInt(rawValue, 10)

  if (Number.isNaN(parsedValue) || parsedValue <= 0) {
    return null
  }

  return parsedValue
}

function parseRetryAfterSeconds(message?: string | null): number | null {
  if (!message) {
    return null
  }

  const secondMatch = message.match(/(\d+)\s*(second|sec|secs|s)\b/i)

  if (secondMatch?.[1]) {
    return Number.parseInt(secondMatch[1], 10)
  }

  const minuteMatch = message.match(/(\d+)\s*(minute|min|mins|m)\b/i)

  if (minuteMatch?.[1]) {
    return Number.parseInt(minuteMatch[1], 10) * 60
  }

  return null
}

export function isAuthRateLimitError(error?: AuthErrorLike | null): boolean {
  if (!error) {
    return false
  }

  if (error.status === 429) {
    return true
  }

  const code = (error.code ?? '').toLowerCase()

  if (code.includes('rate_limit') || code === 'over_email_send_rate_limit' || code === 'over_request_rate_limit') {
    return true
  }

  const message = (error.message ?? '').toLowerCase()

  return message.includes('rate limit') || message.includes('too many requests')
}

export function getSuggestedResetEmailCooldownSeconds(error?: AuthErrorLike | null): number {
  const retryAfterSeconds = parseRetryAfterSeconds(error?.message)

  if (retryAfterSeconds && retryAfterSeconds > 0) {
    return retryAfterSeconds
  }

  return DEFAULT_RESET_EMAIL_COOLDOWN_SECONDS
}

export function getResetEmailCooldownRemainingSeconds(now = Date.now()): number {
  const storage = getStorage()

  if (!storage) {
    return 0
  }

  const cooldownUntil = parseCooldownUntil(storage.getItem(RESET_EMAIL_COOLDOWN_STORAGE_KEY))

  if (!cooldownUntil) {
    storage.removeItem(RESET_EMAIL_COOLDOWN_STORAGE_KEY)
    return 0
  }

  const remainingMs = cooldownUntil - now

  if (remainingMs <= 0) {
    storage.removeItem(RESET_EMAIL_COOLDOWN_STORAGE_KEY)
    return 0
  }

  return Math.ceil(remainingMs / 1000)
}

export function startResetEmailCooldown(seconds = DEFAULT_RESET_EMAIL_COOLDOWN_SECONDS): void {
  const storage = getStorage()

  if (!storage) {
    return
  }

  const safeSeconds = Number.isFinite(seconds)
    ? Math.max(1, Math.floor(seconds))
    : DEFAULT_RESET_EMAIL_COOLDOWN_SECONDS

  storage.setItem(
    RESET_EMAIL_COOLDOWN_STORAGE_KEY,
    String(Date.now() + safeSeconds * 1000)
  )
}

export function getResetEmailErrorMessage(error?: AuthErrorLike | null): string {
  if (!error) {
    return 'Failed to send reset link. Please try again.'
  }

  if (isAuthRateLimitError(error)) {
    const cooldownSeconds = getSuggestedResetEmailCooldownSeconds(error)

    if (cooldownSeconds % 60 === 0 && cooldownSeconds >= 60) {
      const minutes = cooldownSeconds / 60
      return `Too many reset attempts. Please wait ${minutes} minute${minutes === 1 ? '' : 's'} before trying again.`
    }

    return `Too many reset attempts. Please wait ${cooldownSeconds} seconds before trying again.`
  }

  return error.message || 'Failed to send reset link. Please try again.'
}
